import React from "react";
import { Link } from "react-router-dom";
import whiteBankImg from "../../assets/white-bank.webp";
import {
  Users,
  Handshake,
  IndianRupee,
  TrendingUp,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const stats = [
  {
    id: 1,
    value: "10,000+",
    label: "Active DSAs",
    icon: <Users size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 2,
    value: "50+",
    label: "Lending Partners",
    icon: <Handshake size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 3,
    value: "₹15,000 Cr+",
    label: "Loans Facilitated",
    icon: <IndianRupee size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 4,
    value: "98%",
    label: "Payout Accuracy",
    icon: <TrendingUp size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
];

function LendersHero() {
  return (
    <section className="relative overflow-hidden bg-[#f7faf8] pt-5 pb-12 sm:pt-8 sm:pb-16 lg:pt-10 lg:pb-20">
      {/* Soft Background Ambient Radial Gradients */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_35%,rgba(46,229,162,0.12),transparent_40%),radial-gradient(circle_at_88%_45%,rgba(0,135,81,0.08),transparent_40%)]"
      />

      <div className="section-container relative z-10">
        
        {/* ================= 1. BREADCRUMB ================= */}
        <nav className="flex items-center gap-2 font-jakarta text-[12.5px] sm:text-[13px] text-[#6b7280] mb-5 sm:mb-8">
          <Link
            to="/"
            className="hover:text-[var(--hero-green)] transition-colors"
          >
            Home
          </Link>
          <span className="text-[#9ca3af]">›</span>
          <span className="font-semibold text-[#111827]">
            For Lenders
          </span>
        </nav>

        {/* ================= 2. MAIN HERO 2-COLUMN GRID ================= */}
        <div className="grid grid-cols-1 items-center gap-8 sm:gap-10 lg:grid-cols-12 lg:gap-10 xl:gap-14">
          
          {/* LEFT COLUMN: Headline, Value Proposition & Action Button */}
          <div className="lg:col-span-7 xl:col-span-7">
            
            {/* Tag Badge */}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hero-green-light)] px-3 py-1 font-jakarta text-[11.5px] sm:text-[12px] font-bold text-[var(--hero-green)] border border-[var(--hero-green-border)] shadow-xs mb-3.5 sm:mb-4">
              <Sparkles size={13} className="text-[var(--hero-green)]" />
              <span>FOR LENDERS</span>
            </span>

            {/* Main Headline */}
            <h1 className="font-syne text-[28px] sm:text-[38px] md:text-[46px] lg:text-[54px] xl:text-[60px] font-bold leading-[1.15] tracking-[-0.5px] sm:tracking-[-1.5px] text-[#111827] text-balance">
              Lend Smarter. <span className="text-[var(--hero-green)]">Grow Stronger.</span>
            </h1>

            {/* Sub-headline */}
            <p className="mt-4 sm:mt-5 max-w-[580px] font-jakarta text-[15px] sm:text-[16.5px] lg:text-[17px] leading-[1.65] text-[#556369]">
              Finverse connects you with verified DSAs, quality leads, and powerful tools to help you scale lending operations with confidence.
            </p>

            {/* Action Button: Directly jumps to #partner-as-lender id */}
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href="#partner-as-lender"
                className="inline-flex h-[50px] items-center justify-center gap-2.5 rounded-full bg-[var(--hero-green)] px-8 font-jakarta text-[15px] font-bold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-[var(--hero-green-hover)] hover:shadow-lg outline-none focus:outline-none cursor-pointer"
              >
                <span>Partner with Finverse</span>
                <ArrowRight size={17} strokeWidth={2.4} />
              </a>
            </div>

          </div>

          {/* RIGHT COLUMN: 3D White Bank Artwork Pod */}
          <div className="lg:col-span-5 xl:col-span-5 flex items-center justify-center pt-2 lg:pt-0">
            <div className="relative flex items-center justify-center w-full max-w-[300px] sm:max-w-[360px] lg:max-w-[440px]">
              
              {/* Soft Ambient Mint Halo */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute h-[220px] w-[220px] sm:h-[280px] sm:w-[280px] lg:h-[340px] lg:w-[340px] rounded-full bg-[radial-gradient(circle,rgba(46,229,162,0.35),transparent_70%)] blur-2xl"
              />

              {/* 3D White Bank Image */}
              <img
                src={whiteBankImg}
                alt="Finverse Lending Partner Network"
                className="relative z-10 max-h-[250px] sm:max-h-[320px] lg:max-h-[380px] w-auto object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.12)] transition-transform duration-300 hover:scale-105 animate-levitate"
                loading="eager"
              />
            </div>
          </div>

        </div>

        {/* ================= 3. BOTTOM 4 METRIC CARDS ================= */}
        <div className="mt-10 sm:mt-14 lg:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="group relative flex flex-col justify-between rounded-[18px] sm:rounded-[22px] border border-[#dce8e1] bg-white p-3.5 sm:p-5 shadow-[0_3px_15px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--hero-green-border)] hover:shadow-[0_12px_26px_rgba(0,135,81,0.08)] min-w-0"
            >
              <div className="min-w-0">
                {/* Icon Container */}
                <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 aspect-square items-center justify-center rounded-xl bg-[var(--hero-green-light)] border border-[var(--hero-green-border)] text-[var(--hero-green)] mb-2.5 sm:mb-3 shadow-2xs transition-transform duration-300 group-hover:scale-108">
                  {stat.icon}
                </div>

                {/* Big Metric Value */}
                <p className="font-bricolage text-[19px] sm:text-[24px] lg:text-[26px] font-bold leading-tight tracking-tight text-[#111827] break-words">
                  {stat.value}
                </p>

                {/* Label */}
                <p className="mt-1 font-jakarta text-[11.5px] sm:text-[13px] font-medium text-[#556369] leading-tight">
                  {stat.label}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Levitate Keyframe Animation */}
      <style>{`
        @keyframes levitateBank {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-7px);
          }
        }
        .animate-levitate {
          animation: levitateBank 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}

export default LendersHero;
